import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { getRepositories, getAccessRequests, getRecentActivity, getMyAccess, getMyExpiredAccess } from '../services/api';
import { formatAccessExpiry } from '../utils/formatAccessExpiry';
import './Dashboard.css';

const Dashboard = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalRepos: 0,
    privateRepos: 0,
    publicRepos: 0,
    pendingRequests: 0
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [myAccess, setMyAccess] = useState([]);
  const [expiredAccess, setExpiredAccess] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [loadingStats, setLoadingStats] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      setLoadingStats(true);
      const [reposResponse, requestsResponse, activityRes, myAccessRes, expiredRes] = await Promise.all([
        getRepositories().catch(() => ({ data: [] })),
        getAccessRequests().catch(() => ({ data: [] })),
        getRecentActivity(8).catch(() => ({ data: [] })),
        getMyAccess().catch(() => ({ data: [] })),
        getMyExpiredAccess().catch(() => ({ data: [] }))
      ]);

      const repos = reposResponse.data;
      const requests = requestsResponse.data;

      setStats({
        totalRepos: repos.length,
        privateRepos: repos.filter(r => r.private).length,
        publicRepos: repos.filter(r => !r.private).length,
        pendingRequests: requests.filter(r => r.status === 'pending').length
      });
      setRecentActivity(Array.isArray(activityRes.data) ? activityRes.data : []);
      setMyAccess(myAccessRes.data || []);
      setExpiredAccess(expiredRes.data || []);
      const mine = user?.role === 'manager' || user?.role === 'admin'
        ? requests.filter((r) => r.status === 'pending' && r.canReview !== false && !r.isOwnRequest)
        : [];
      setPendingApprovals(mine.slice(0, 8));
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoadingStats(false);
    }
  }, [user]);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
      return;
    }

    if (user) {
      fetchStats();
    }
  }, [user, loading, navigate, fetchStats]);

  if (loading || !user) {
    return (
      <div className="dashboard">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <header className="page-intro">
        <h1 className="page-title">Hello, {user.displayName || user.username}</h1>
        <p className="page-lead">
          See the repositories you can use, or open a request that is waiting for a decision.
        </p>
      </header>
      {loadingStats && <p className="empty-text">Loading dashboard…</p>}

      {(user.role === 'manager' || user.role === 'admin') && stats.pendingRequests > 0 && (
        <div className="attention-banner">
          <div>
            <strong>
              {stats.pendingRequests} request{stats.pendingRequests === 1 ? '' : 's'} waiting
            </strong>
            <p>Approve access, or create a repository, from the requests page.</p>
          </div>
          <button className="btn btn-primary" type="button" onClick={() => navigate('/access-requests')}>
            Review now
          </button>
        </div>
      )}

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">📦</div>
          <div className="stat-content">
            <div className="stat-value">{stats.totalRepos}</div>
            <div className="stat-label">Total Repositories</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">🔒</div>
          <div className="stat-content">
            <div className="stat-value">{stats.privateRepos}</div>
            <div className="stat-label">Private Repositories</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">🌐</div>
          <div className="stat-content">
            <div className="stat-value">{stats.publicRepos}</div>
            <div className="stat-label">Public Repositories</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">📋</div>
          <div className="stat-content">
            <div className="stat-value">{stats.pendingRequests}</div>
            <div className="stat-label">Pending Requests</div>
          </div>
        </div>
      </div>

      <div className="dashboard-actions">
        <button
          className="btn btn-primary"
          onClick={() => navigate('/repositories')}
        >
          Request repository access
        </button>
        <button
          className="btn btn-secondary"
          onClick={() => navigate('/access-requests')}
        >
          My access requests
        </button>
        <Link to="/notifications" className="btn btn-secondary">Notifications</Link>
      </div>

      <div className="dashboard-panels">
        {(user?.role === 'manager' || user?.role === 'admin') && (
          <section className="dashboard-panel card">
            <h2>{user.role === 'manager' ? 'Approvals waiting for you' : 'Pending approvals'}</h2>
            {pendingApprovals.length === 0 ? (
              <p className="empty-text">No requests waiting for your decision</p>
            ) : (
              <ul className="my-access-list">
                {pendingApprovals.slice(0, 8).map((req) => (
                  <li key={req._id}>
                    <span>{req.repository?.fullName || 'Repository'} — {req.requestedFor}</span>
                    <span className="perm-tag">{req.permission}</span>
                  </li>
                ))}
              </ul>
            )}
            <Link to="/access-requests" className="view-all-link">Open approvals →</Link>
          </section>
        )}

        {expiredAccess.length > 0 && (
          <section className="dashboard-panel card">
            <h2>Access that expired</h2>
            <ul className="my-access-list">
              {expiredAccess.slice(0, 6).map((a) => (
                <li key={a._id}>
                  <span>{a.repository?.fullName || 'Repository'}</span>
                  {a.repository?.owner && a.repository?.name ? (
                    <Link to={`/repositories/${a.repository.owner}/${a.repository.name}`}>Request again</Link>
                  ) : null}
                </li>
              ))}
            </ul>
          </section>
        )}

        {myAccess.length > 0 && (
          <section className="dashboard-panel card">
            <h2>My repository access</h2>
            <ul className="my-access-list">
              {myAccess.slice(0, 8).map((repo) => (
                <li key={repo._id}>
                  <Link to={`/repositories/${repo.owner}/${repo.name}`}>{repo.fullName}</Link>
                  <span className={`perm-tag perm-${repo.userPermission}`}>{repo.userPermission}</span>
                  {repo.accessExpiresAt && (
                    <span className="perm-tag">Expires {formatAccessExpiry(repo.accessExpiresAt)}</span>
                  )}
                </li>
              ))}
            </ul>
            {myAccess.length > 8 && (
              <Link to="/repositories" className="view-all-link">View all →</Link>
            )}
          </section>
        )}

        <section className="dashboard-panel card">
          <h2>Recent activity</h2>
          {recentActivity.length === 0 ? (
            <p className="empty-text">No recent activity</p>
          ) : (
            <ul className="activity-list">
              {recentActivity.map((log) => (
                <li key={log._id}>
                  <span className="activity-action">{log.action?.replace(/_/g, ' ')}</span>
                  <span className="activity-date">{log.createdAt ? new Date(log.createdAt).toLocaleString() : ''}</span>
                </li>
              ))}
            </ul>
          )}
          {user?.role === 'admin' && (
            <Link to="/admin/activity-logs" className="view-all-link">View audit log →</Link>
          )}
        </section>
      </div>
    </div>
  );
};

export default Dashboard;
